// F-WEB-005 NotificationBell real (W6).
// API-080/081/082 + poll 60s + dropdown click-outside.
import { Bell, CheckCheck, ExternalLink, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type { AppNotification, UnreadCountResponse } from "../../lib/types";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Spinner } from "../ui/spinner";
import { relativeTime } from "../../lib/utils";
import { cn } from "../../lib/utils";
import { labelNotification, notificationLink, summarizeNotification } from "../../lib/notifications";

const POLL_INTERVAL_MS = 60_000;
const DROPDOWN_LIMIT = 10;

export function NotificationBell(): JSX.Element {
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const qc = useQueryClient();

  const { data: unread } = useQuery<UnreadCountResponse>({
    queryKey: ["notif-unread-count"],
    queryFn: async () => {
      const r = await api.get<UnreadCountResponse>("/api/notifications/unread-count");
      return r.data;
    },
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  const { data: list = [], isFetching } = useQuery<AppNotification[]>({
    queryKey: ["notif-list", DROPDOWN_LIMIT],
    queryFn: async () => {
      const r = await api.get<AppNotification[]>(
        `/api/notifications?limit=${DROPDOWN_LIMIT}`,
      );
      return r.data;
    },
    enabled: open,
  });

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (
        popRef.current?.contains(e.target as Node) ||
        buttonRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      await api.patch(`/api/notifications/${id}`, { read: true });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread-count"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });

  const markAll = useMutation({
    mutationFn: async () => {
      await api.post("/api/notifications/mark-all-read", {});
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread-count"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });

  const count = unread?.count ?? 0;

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        variant="ghost"
        size="icon"
        aria-label={`Notificações (${count} não lidas)`}
        title="Notificações"
        onClick={() => setOpen((o) => !o)}
        className="relative"
      >
        <Bell className="icon" aria-hidden />
        {count > 0 ? (
          <span
            aria-hidden
            className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-bad text-white text-micro font-extrabold leading-4 text-center"
          >
            {count > 99 ? "99+" : count}
          </span>
        ) : null}
      </Button>

      {open ? (
        <div
          ref={popRef}
          role="dialog"
          aria-label="Notificações"
          className={cn(
            "absolute right-0 mt-2 w-[min(22rem,calc(100vw-2rem))] z-50",
            "rounded-md border border-line bg-card shadow-overlay",
          )}
        >
          <header className="spread" style={{ padding: "var(--sp-2) var(--sp-3)", borderBottom: "var(--border)" }}>
            <span className="text-md font-extrabold text-ink">Notificações</span>
            <div className="cluster" style={{ gap: ".25rem" }}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAll.mutate()}
                disabled={count === 0 || markAll.isPending}
                title="Marcar todas como lidas"
              >
                <CheckCheck className="icon" aria-hidden />
                <span className="sr-only">Marcar todas como lidas</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                title="Fechar"
              >
                <X className="icon" aria-hidden />
                <span className="sr-only">Fechar</span>
              </Button>
            </div>
          </header>

          <div className="max-h-[60vh] overflow-y-auto" data-testid="notif-list">
            {isFetching && list.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            ) : list.length === 0 ? (
              <div className="field__hint" style={{ textAlign: "center", padding: "var(--sp-4) var(--sp-4)" }}>
                Nenhuma notificação.
              </div>
            ) : (
              <ul>
                {list.map((n) => (
                  <li
                    key={n.id}
                    className="stack"
                    style={{ gap: ".375rem", padding: "var(--sp-2) var(--sp-3)", borderBottom: "var(--border)" }}
                  >
                    <div className="cluster" style={{ gap: ".375rem" }}>
                      {!n.read ? (
                        <span
                          aria-hidden
                          style={{ width: ".375rem", height: ".375rem", borderRadius: "50%", background: "var(--color-coral)", flexShrink: 0 }}
                        />
                      ) : null}
                      <Badge variant={n.type === "sync_failed" ? "error" : "outline"}>
                        {labelNotification(n)}
                      </Badge>
                      <span className="field__hint" style={{ whiteSpace: "nowrap" }}>{relativeTime(n.created_at)}</span>
                    </div>
                    <div className="text-md text-ink" style={{ wordBreak: "break-word" }}>
                      {summarizeNotification(n)}
                    </div>
                    {notificationLink(n) ? (
                      <a
                        href={notificationLink(n)!.href}
                        target="_blank"
                        rel="noreferrer"
                        className="text-coral underline underline-offset-2 hover:no-underline text-md"
                      >
                        {notificationLink(n)!.label}
                      </a>
                    ) : null}
                    {!n.read ? (
                      <button
                        type="button"
                        className="btn btn--ghost"
                        style={{ minHeight: "auto", padding: 0, alignSelf: "flex-start" }}
                        onClick={() => markRead.mutate(n.id)}
                        title="Marcar como lida"
                        aria-label="Marcar como lida"
                      >
                        <CheckCheck className="icon" aria-hidden />
                        Marcar lida
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <footer style={{ borderTop: "var(--border)", padding: "var(--sp-2) var(--sp-3)" }}>
            <Link
              to="/admin/notificacoes"
              onClick={() => setOpen(false)}
              className="cluster text-coral"
              style={{ gap: ".25rem" }}
            >
              Ver caixa de entrada completa
              <ExternalLink className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden />
            </Link>
          </footer>
        </div>
      ) : null}
    </div>
  );
}

